import React, { useEffect, useState } from 'react';
import { AlertTriangle, ImageOff, Maximize2, RotateCw, X, ZoomIn, ZoomOut } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// DOC-LIFECYCLE-001 (Phase D+E) — Eine Medien-Fläche: Bild oder PDF aus einer lokalen
// blob:-URL (der Aufrufer lädt per Authorization-Header und verwaltet revoke über die
// blobRegistry). Jede Fläche kann zoomen, drehen und in den Vollbild-Modus wechseln;
// fehlende Inhalte zeigen einen EXPLIZITEN missing-State (die Fläche verschwindet nie).
// ─────────────────────────────────────────────────────────────────────────────

const ctlBtn = { padding: '5px 7px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 7, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 };

export default function MediaPane({ label, sub, url, mime, loading, missing, missingText, height = 240, footer }) {
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [full, setFull] = useState(false);

  // Vollbild: Escape schließt (CSS-Overlay statt Fullscreen-API — deterministisch, kein Browser-Prompt).
  useEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [full]);

  const isPdf = (mime || '').includes('pdf');

  const media = (big) => {
    if (missing) return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, height: '100%', color: '#b91c1c', background: '#fef2f2', borderRadius: 10, padding: 12, textAlign: 'center' }}>
        <ImageOff size={22} />
        <div style={{ fontSize: 12, fontWeight: 800 }}>Fehlt</div>
        {missingText && <div style={{ fontSize: 12, color: '#991b1b' }}>{missingText}</div>}
      </div>
    );
    if (loading || !url) return (
      <div className="vg-sk" style={{ height: '100%', borderRadius: 10 }} />
    );
    if (isPdf) return (
      <iframe title={label} src={url} style={{ width: '100%', height: '100%', border: 'none', borderRadius: 10, background: '#fff' }} />
    );
    return (
      <div style={{ height: '100%', overflow: 'auto', borderRadius: 10, background: '#f3f4f6', display: 'flex', alignItems: zoom > 1 ? 'flex-start' : 'center', justifyContent: zoom > 1 ? 'flex-start' : 'center' }}>
        <img src={url} alt={label}
          style={{ maxWidth: zoom === 1 ? '100%' : 'none', maxHeight: zoom === 1 ? '100%' : 'none',
            width: zoom === 1 ? undefined : `${zoom * 100}%`,
            transform: `rotate(${rot}deg)`, transition: 'transform .15s', display: 'block', margin: 'auto' }} />
      </div>
    );
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 10, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#0a0a0a' }}>{label}</div>
          {sub && <div style={{ fontSize: 12, color: '#4b5563', marginTop: 1 }}>{sub}</div>}
        </div>
        {!missing && !loading && url && (
          <div style={{ display: 'flex', gap: 4 }}>
            {!isPdf && <>
              <button title="Vergrößern" onClick={() => setZoom((z) => Math.min(5, +(z + 0.5).toFixed(2)))} style={ctlBtn}><ZoomIn size={13} /></button>
              <button title="Verkleinern" onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(2)))} style={ctlBtn}><ZoomOut size={13} /></button>
              <button title="Drehen (90°)" onClick={() => setRot((r) => (r + 90) % 360)} style={ctlBtn}><RotateCw size={13} /></button>
            </>}
            <button title="Vollbild" onClick={() => setFull(true)} style={ctlBtn}><Maximize2 size={13} /></button>
          </div>
        )}
        {missing && <AlertTriangle size={14} color="#ef4444" />}
      </div>
      <div style={{ height }}>{media(false)}</div>
      {footer}

      {full && (
        <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(10,10,10,.92)', zIndex: 300, display: 'flex', flexDirection: 'column', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>{label}</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {!isPdf && <>
                <button onClick={() => setZoom((z) => Math.min(5, +(z + 0.5).toFixed(2)))} style={ctlBtn}><ZoomIn size={14} /></button>
                <button onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(2)))} style={ctlBtn}><ZoomOut size={14} /></button>
                <button onClick={() => setRot((r) => (r + 90) % 360)} style={ctlBtn}><RotateCw size={14} /></button>
              </>}
              <button onClick={() => setFull(false)} style={ctlBtn}><X size={14} /> Schließen (Esc)</button>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0 }}>{media(true)}</div>
        </div>
      )}
    </div>
  );
}
